import { AloftPrice, MLSListing } from '@aloft/toolkit-shared';
import { Flex, IconButton, Typography } from '@aloft/toolkit-sui';

import {
  setIsEditPropertyDetailsModalOpen,
  setObjectiveEditMode,
} from '../../../redux/features/uiSlice.ts';
import { useAppDispatch } from '../../../redux/hooks.ts';
import { MLSDataSheetParams } from '../MLSDataSheet/types.ts';
import { ReferenceViewID, ReferenceViewPropertyIDParsed, parseReferenceViewID } from '../index.ts';
import { MLSListingID } from './MLSListingID.tsx';

export const MLSDataHeader = ({
  isPopout,
  referenceViewID,
  listingData,
  headerType,
  mlsDataSheetParams,
}: {
  isPopout: boolean;
  referenceViewID: ReferenceViewID;
  listingData: MLSListing;
  headerType: 'data' | 'images';
  mlsDataSheetParams: MLSDataSheetParams;
}) => {
  const dispatch = useAppDispatch();
  const { openPopup, orderID } = mlsDataSheetParams;
  const isSold = ['sold', 'closed'].includes(listingData.MlsStatus?.toLowerCase() ?? '');
  const closePriceStr = listingData.ClosePrice
    ? AloftPrice.fromNumericPrice(listingData.ClosePrice).toString(true)
    : '-';
  const listPriceStr = listingData.ListPrice
    ? AloftPrice.fromNumericPrice(listingData.ListPrice).toString(true)
    : '-';

  const parsedID = parseReferenceViewID(referenceViewID) as ReferenceViewPropertyIDParsed | null;

  const openEditPropertyDetailsModal = () => {
    dispatch(
      setObjectiveEditMode(parsedID?.type === 'subject' ? 'subject' : (parsedID?.compID ?? '')),
    );
    dispatch(setIsEditPropertyDetailsModalOpen(true));
  };

  return (
    <Flex width="100%" justifyContent="space-between">
      <Flex flexDirection="column">
        <Flex alignItems="center">
          <MLSListingID
            listingData={listingData}
            isPopout={isPopout}
            referenceViewID={referenceViewID}
            headerType={headerType}
            mlsDataSheetParams={mlsDataSheetParams}
          />
          <Flex width=".5rem" />
          {!isPopout && (
            <IconButton
              size="sm"
              variant="tertiary"
              icon="pencil"
              onClick={openEditPropertyDetailsModal}
              ariaLabel="edit property details"
              iconSize="lg"
            />
          )}
          {!isPopout && headerType === 'data' && listingData.ListingId && (
            <IconButton
              size="sm"
              variant="tertiary"
              icon="image"
              __testId="image-button"
              onClick={() => {
                openPopup(
                  `/reference-data/${orderID}/craftsman/mls-images/${referenceViewID}`,
                  '_blank',
                  'rel=noopener noreferrer',
                );
              }}
              ariaLabel="open in popout window"
              iconSize="lg"
            />
          )}
        </Flex>
        <Flex justifyContent="flex-start" flexDirection="column">
          <Typography variant="h2">{listingData.UnparsedAddress || ''}</Typography>
          <Typography variant="h3" color="textTertiary">{`${listingData.City ?? ''}${
            listingData.City && listingData.StateOrProvince ? ', ' : ''
          }${listingData.StateOrProvince ?? ''}${listingData.PostalCode ? ` ${listingData.PostalCode}` : ''}`}</Typography>
        </Flex>
      </Flex>
      <Flex justifyContent="space-between" flexDirection="column">
        <Flex>
          <Typography variant="body1" color="textTertiary">
            Last Modified at&nbsp;
            {listingData.ModificationTimestamp
              ? new Date(listingData.ModificationTimestamp).toLocaleString()
              : 'N/A'}
          </Typography>
        </Flex>
        <Flex justifyContent="flex-end" alignItems="flex-end" flexDirection="column">
          <Typography variant="h2">{isSold ? closePriceStr : listPriceStr}</Typography>
          <Typography variant="h5" color="textTertiary">
            {listingData.MlsStatus ?? ''}
          </Typography>
        </Flex>
      </Flex>
    </Flex>
  );
};
